import NewWindow from 'components/Icons/NewWindow';
import BaseInput from 'components/inputs/BaseInput';
import Label from 'components/inputs/Label';
import { environmentVars } from 'constants/services';
import AppStoreContext, { AppStore } from 'contexts/AppStoreContext';
import React, { useContext, useState } from 'react';
import { Project } from 'records/Project';
import { RoleMap } from 'records/Role';
import { User } from 'records/User';
import { RawUserProfile, UserProfile } from 'records/UserProfile';
import { ModalStaticProperties } from 'components/ModalPortal/ModalPortal';
import { useTranslator } from '@bluebeam/react-i18n';
import ModalStdHeader from './ModalStdHeader';

interface Props {
  user: User;
  roles: RoleMap;
  project: Project;
}

const accountLink = environmentVars.BB_OPENID_CREATE_USER_ENDPOINT.substr(
  0,
  environmentVars.BB_OPENID_CREATE_USER_ENDPOINT.length -
    '/createaccount'.length
);

const EditUserProfileInfoModal: React.FC<Props> & ModalStaticProperties = (
  props
) => {
  const [profile, setProfile] = useState<UserProfile>(
    props.user.profile.set('additionalinfoneeded', false)
  );

  const { translate } = useTranslator(['userProfile', 'buttons', 'formLabels']);

  const { actions } = useContext(AppStoreContext);

  const handleCancelClick = (): void => {
    const { clearAllModals } = actions;

    clearAllModals();
  };

  const handleChange = (
    fieldName: keyof RawUserProfile,
    /* evt: React.ChangeEvent<HTMLInputElement> */
    value: string
  ): void => {
    setProfile(profile.set(fieldName, value));
  };

  const handleCreateUserProfile = async (
    evt: React.FormEvent
  ): Promise<void> => {
    evt.preventDefault();

    const { createUserProfile, clearAllModals } = actions;

    await createUserProfile(profile);

    clearAllModals();
  };

  const { user, roles, project } = props;
  let userRole = roles.getIn([user.id, 'role']);
  userRole = userRole.charAt(0).toUpperCase() + userRole.slice(1);

  return (
    <div id="enter-user-profile-info-modal">
      <ModalStdHeader text={translate('userProfile:editProfile')} />
      <div>
        <form>
          <div className="column">
            <div className="h100 px5 pt8 pb4">
              <div className="pb4" style={{ fontWeight: 300 }}>
                <div className="color-black-2 font-size-7 semi-bold pb2">
                  {translate('userProfile:profileDetails')}
                </div>
                <div className="color-black-1 font-size-5 pb3">
                  {translate('userProfile:updateDetails')}
                </div>
                <div className="color-black-1 font-size-5 pb2">
                  {translate('userProfile:updateNameEmail')}

                  <a
                    target="_blank"
                    href={accountLink}
                    style={{ color: '#0074cc', textDecoration: 'underline' }}
                    rel="noreferrer"
                  >
                    {translate('userProfile:accountSettings')}
                    <NewWindow fill="#0074cc" width="25" height="19" />
                  </a>
                </div>
              </div>
              <div
                className="p3 mb3 b-solid b-radius-3"
                style={{
                  backgroundColor: 'var(--light-grey-2)',
                  borderColor: 'var(--light-grey-1)',
                  borderWidth: '0.5px',
                }}
              >
                <div className="color-black-2 font-size-5 semi-bold pb2">
                  {project.name}
                </div>
                <div className="color-black-1 font-size-4">
                  {translate('userProfile:permissionLevel')} {userRole}
                </div>
              </div>
              <div
                className="p3 b-solid b-radius-3"
                style={{
                  backgroundColor: 'var(--light-grey-2)',
                  borderColor: 'var(--light-grey-1)',
                  borderWidth: '0.5px',
                }}
              >
                <div className="mb4 color-gray-7 font-size-5">
                  <div className="pb2">{translate('formLabels:name')}</div>
                  <BaseInput
                    id="profile-info-modal-email"
                    name="email"
                    value={`${profile.firstname} ${profile.lastname}`}
                    disabled
                  />
                </div>
                <div className="mb4 color-gray-7 font-size-5">
                  <div className="pb2">{translate('formLabels:email')}</div>
                  <BaseInput
                    id="profile-info-modal-email"
                    name="email"
                    value={user.email}
                    disabled
                  />
                </div>
                <div className="row mb2">
                  <div>
                    <Label label={translate('formLabels:company')} />
                    <BaseInput
                      id="profile-info-modal-company"
                      type="text"
                      name="company"
                      value={profile.company}
                      onChange={(evt): void =>
                        handleChange('company', evt.target.value)
                      }
                    />
                  </div>
                  <div className="ml3">
                    <Label label={translate('formLabels:jobTitle')} />
                    <BaseInput
                      id="profile-info-modal-role"
                      type="text"
                      name="role"
                      value={profile.role}
                      onChange={(evt): void =>
                        handleChange('role', evt.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="row justify-between pr4 pl4 pt3 pb1 mb2 bt-2">
        <button
          id="profile-info-modal-cancel-btn"
          className="btn-lg btn-link mr2"
          onClick={handleCancelClick}
        >
          {translate('buttons:cancel')}
        </button>
        <button
          id="profile-info-modal-confirm-btn"
          className="btn-lg btn-primary"
          onClick={handleCreateUserProfile}
        >
          {translate('buttons:save')}
        </button>
      </div>
    </div>
  );
};

EditUserProfileInfoModal.modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    top: 'calc(50vh - 350px)',
    left: 'calc(50vw - 270px)',
    bottom: 'initial',
    right: 'initial',
    width: '540px',
    padding: '0px',
  },
};

export default EditUserProfileInfoModal;
